import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class SoundNotificationsService {
  private readonly STORAGE_KEY = 'sound-notifications-enabled';
  private readonly MUTED_NOTIFICATIONS_KEY = 'muted-notifications';
  private soundEnabledSubject = new BehaviorSubject<boolean>(true);
  public soundEnabled$ = this.soundEnabledSubject.asObservable();

  constructor() {
    this.loadSoundSettings();
  }

  private loadSoundSettings(): void {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored !== null) {
        const enabled = JSON.parse(stored);
        this.soundEnabledSubject.next(enabled);
      } else {
        // Padrão: som ativado
        this.soundEnabledSubject.next(true);
        this.saveSoundSettings(true);
      }
    } catch (error) {
      console.error('Erro ao carregar configurações de som:', error);
      this.soundEnabledSubject.next(true);
    }
  }

  private saveSoundSettings(enabled: boolean): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(enabled));
    } catch (error) {
      console.error('Erro ao salvar configurações de som:', error);
    }
  }

  setSoundEnabled(enabled: boolean): void {
    this.soundEnabledSubject.next(enabled);
    this.saveSoundSettings(enabled);
  }

  getSoundEnabled(): boolean {
    return this.soundEnabledSubject.value;
  }

  playNotificationSound(icon?: string, notificationRef?: string): void {
    // Verificar se o som está ativado globalmente
    if (!this.getSoundEnabled()) {
      return;
    }

    // Verificar se a notificação está silenciada
    if (notificationRef && this.isNotificationMuted(notificationRef)) {
      return;
    }

    try {
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      // Configurar som baseado no ícone da notificação
      switch (icon) {
        case 'bi-exclamation-triangle-fill': // Chamado urgente
          this.playUrgentSound(oscillator, gainNode, audioContext);
          break;
        case 'bi-bug-fill': // Chamado com defeito
          this.playBugSound(oscillator, gainNode, audioContext);
          break;
        default: // Som padrão de campainha
          this.playDefaultSound(oscillator, gainNode, audioContext);
          break;
      }
    } catch (error) {
      console.error('Erro ao reproduzir som de notificação:', error);
    }
  }

  private isNotificationMuted(notificationRef: string): boolean {
    try {
      const stored = localStorage.getItem(this.MUTED_NOTIFICATIONS_KEY);
      if (stored) {
        const mutedList = JSON.parse(stored);
        return mutedList.includes(notificationRef);
      }
      return false;
    } catch (error) {
      console.error('Erro ao verificar notificações silenciadas:', error);
      return false;
    }
  }

  private playDefaultSound(
    oscillator: OscillatorNode,
    gainNode: GainNode,
    audioContext: AudioContext
  ): void {
    // Som padrão de campainha
    oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
    oscillator.frequency.setValueAtTime(600, audioContext.currentTime + 0.1);
    oscillator.frequency.setValueAtTime(800, audioContext.currentTime + 0.2);

    gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);

    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.3);
  }

  private playUrgentSound(
    oscillator: OscillatorNode,
    gainNode: GainNode,
    audioContext: AudioContext
  ): void {
    // Som de urgência - tom mais agudo e repetitivo
    oscillator.frequency.setValueAtTime(1000, audioContext.currentTime);
    oscillator.frequency.setValueAtTime(1200, audioContext.currentTime + 0.1);
    oscillator.frequency.setValueAtTime(1000, audioContext.currentTime + 0.2);
    oscillator.frequency.setValueAtTime(1200, audioContext.currentTime + 0.3);
    oscillator.frequency.setValueAtTime(1000, audioContext.currentTime + 0.4);

    gainNode.gain.setValueAtTime(0.4, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);

    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.5);
  }

  private playBugSound(
    oscillator: OscillatorNode,
    gainNode: GainNode,
    audioContext: AudioContext
  ): void {
    // Som de defeito - tom mais grave e descendente
    oscillator.frequency.setValueAtTime(400, audioContext.currentTime);
    oscillator.frequency.setValueAtTime(350, audioContext.currentTime + 0.1);
    oscillator.frequency.setValueAtTime(300, audioContext.currentTime + 0.2);
    oscillator.frequency.setValueAtTime(250, audioContext.currentTime + 0.3);

    gainNode.gain.setValueAtTime(0.35, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.4);

    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.4);
  }
}
